'use client';

import { useTheme } from 'next-themes';
import { Toaster as Sonner, type ToasterProps } from 'sonner';
import {
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
  OctagonXIcon,
} from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';

const Toaster = ({ ...props }: ToasterProps) => {
  // Eclipse forces the light theme; a stale stored preference must not
  // switch the toasts to Sonner's dark palette on light surfaces.
  const { forcedTheme, theme = 'system' } = useTheme();

  return (
    <Sonner
      theme={(forcedTheme ?? theme) as ToasterProps['theme']}
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Spinner className="size-4 animate-spin" />,
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          // Sonner's own focus style is a faint gray ring in an unlayered
          // sheet, so the Eclipse gold ring needs `!` to win. Undo toasts move
          // keyboard focus onto the toast when their action folds away.
          toast: 'cn-toast focus-visible:ring-2! focus-visible:ring-ring',
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
